import * as _ from 'lodash';

import { Attribute } from './attribute';
import { AttributeUiType } from './attributeUiType';
import { AttributeDefinition } from './attributeDefinition';
import { ComplexAttributeSubFieldValue } from './complexAttributeSubFieldValue';
import { FieldDisplayPipe } from '../../pipes/field-display.pipe';

export class FlatAttribute {
  public attributeDefinitionId: string;
  public name: string;
  public description: string;
  public adminDocumentation: string;
  public type: string;
  public attributes: Attribute[];
  public complexAttributeValues: FlatComplexAttributeValue[];
  public isSkill: boolean;
  public isStarRating: boolean;
  public isDropDown: boolean;
  public isDateField: boolean;
  public isLongAnswer: boolean;
  public isEncryptedField: boolean;
  public isDocument: boolean;
  public isComplexField: boolean;
  public createdBy: string;
  public createdOn: Date;
  public updatedBy: string;
  public updatedOn: Date;
  public hasAttributes: boolean;
  public hasComplexAttributeValues: boolean;
  public values: Array<string>;
  public formattedValues: string;
  private getCreatedBy(): string {
    return this.hasAttributes
      ? this.attributes[0].createdBy
      : this.hasComplexAttributeValues
      ? this.complexAttributeValues[0].createdBy
      : null;
  }

  private getCreatedOn(): Date {
    return this.hasAttributes
      ? this.attributes[0].createdOn
      : this.hasComplexAttributeValues
      ? this.complexAttributeValues[0].createdOn
      : null;
  }

  private getUpdatedBy(): string {
    return this.hasAttributes
      ? this.attributes[0].updatedBy
      : this.hasComplexAttributeValues
      ? this.complexAttributeValues[0].updatedBy
      : null;
  }

  private getUpdatedOn(): Date {
    return this.hasAttributes
      ? this.attributes[0].updatedOn
      : this.hasComplexAttributeValues
      ? this.complexAttributeValues[0].updatedOn
      : null;
  }

  private getHasAttributes(): boolean {
    return this.attributes?.length > 0;
  }
  private getHasComplexAttributeValues(): boolean {
    return this.complexAttributeValues?.length > 0;
  }
  private getValues(): Array<string> {
    if (this.type == AttributeUiType.Checkboxes || this.type == AttributeUiType.MultiSelect) {
      return _.flatMap(this.attributes, (a) => a.attributeDefinitionValueListItem || a.valueList)
        .map((a) => a.itemValue)
        .filter((a) => a);
    }

    if (this.type == AttributeUiType.DropDown) {
      var value = _.first(this.attributes);
      return [
        value && value.attributeDefinitionValueListItem
          ? value.attributeDefinitionValueListItem.itemValue
          : '',
      ];
    }

    return _.map(this.attributes, (a) => a.value).filter((a) => a);
  }

  private getFormattedValues(): string {
    return this.values.join(', ');
  }
  public static fromAttributeDefinition(attributeDefinitiion: AttributeDefinition): FlatAttribute {
    let attr = new FlatAttribute();
    if (attributeDefinitiion) {
      attr.attributeDefinitionId = attributeDefinitiion.id;
      attr.type = attributeDefinitiion.uiType;
      attr.name = attributeDefinitiion.name;
      attr.description = attributeDefinitiion.description;
      attr.adminDocumentation = attributeDefinitiion.adminDocumentation;
      attr.attributes = [];
      attr.complexAttributeValues = [];
      //order of the codes here is improtant
      attr.isSkill = attr.type === AttributeUiType.Skill;
      attr.isStarRating = attr.type === AttributeUiType.StarRating;
      attr.isDropDown = attr.type === AttributeUiType.DropDown;
      attr.isDateField = attr.type === AttributeUiType.Date;
      attr.isLongAnswer = attr.type === AttributeUiType.LongAnswer;
      attr.isEncryptedField = attr.type === AttributeUiType.EncryptedField;
      attr.isDocument = attr.type === AttributeUiType.Document;
      attr.isComplexField = attr.type === AttributeUiType.Complex;
      attr.hasAttributes = attr.getHasAttributes(); // this should be called before geting dates
      attr.hasComplexAttributeValues = attr.getHasComplexAttributeValues(); // this should be called before geting dates
      attr.values = attr.getValues();
      attr.formattedValues = attr.getFormattedValues();
      attr.createdBy = attr.getCreatedBy();
      attr.createdOn = attr.getCreatedOn();
      attr.updatedBy = attr.getUpdatedBy();
      attr.updatedOn = attr.getUpdatedOn();
    }
    return attr;
  }

  public static fromAttributes(attrs: Attribute[]): FlatAttribute[] {
    var byType = _.groupBy(attrs, 'attributeDefinitionId');

    var flatModel = _.map(byType, function (v, k) {
      var attr = new FlatAttribute();
      attr.type = _.first(v).type;

      attr.attributeDefinitionId = k;
      attr.name = _.first(v).name;
      attr.description = _.first(v).description;
      attr.adminDocumentation = _.first(v).adminDocumentation;
      if (attr.type == AttributeUiType.Complex) {
        var complex = attrs
          .filter((a) => a.attributeDefinitionId == k && a.complexAttributeSubFieldValue)
          .map((a) => a.complexAttributeSubFieldValue);
        attr.complexAttributeValues = FlatAttribute.flatComplexValues(complex);
        attr.complexAttributeValues.forEach((a) => {
          a.createdBy = _.first(v).createdBy;
          a.createdOn = _.first(v).createdOn;
          a.updatedBy = _.first(v).updatedBy;
          a.updatedOn = _.first(v).updatedOn;
        });
      } else {
        attr.attributes = _.sortBy(
          v,
          (a) => a.attributeDefinitionValueListItem && a.attributeDefinitionValueListItem.rank
        );
      }
      //order of the codes here is improtant
      attr.isSkill = attr.type === AttributeUiType.Skill;
      attr.isStarRating = attr.type === AttributeUiType.StarRating;
      attr.isDropDown = attr.type === AttributeUiType.DropDown;
      attr.isDateField = attr.type === AttributeUiType.Date;
      attr.isLongAnswer = attr.type === AttributeUiType.LongAnswer;
      attr.isEncryptedField = attr.type === AttributeUiType.EncryptedField;
      attr.isDocument = attr.type === AttributeUiType.Document;
      attr.isComplexField = attr.type === AttributeUiType.Complex;
      attr.hasAttributes = attr.getHasAttributes(); // this should be called before geting dates
      attr.hasComplexAttributeValues = attr.getHasComplexAttributeValues(); // this should be called before geting dates
      attr.values = attr.getValues();
      attr.formattedValues = attr.getFormattedValues();
      attr.createdBy = attr.getCreatedBy();
      attr.createdOn = attr.getCreatedOn();
      attr.updatedBy = attr.getUpdatedBy();
      attr.updatedOn = attr.getUpdatedOn();
      return attr;
    });

    return flatModel;
  }
  public static flatComplexValues(
    attrs: ComplexAttributeSubFieldValue[]
  ): FlatComplexAttributeValue[] {
    var byGroupId = _.groupBy(attrs, 'groupId');
    var flat = _.map(byGroupId, function (v, k) {
      var attr = new FlatComplexAttributeValue();
      attr.groupId = parseInt(k);
      var bySubFieldDefinitionItemId = _.groupBy(v, 'subFieldDefinitionItemId');
      var items = _.map(bySubFieldDefinitionItemId, function (v2, k2) {
        return new ComplexAttributeItems(
          k2,
          v2,
          _.first(v2).name,
          _.first(v2).type,
          _.first(v2).rank
        );
      });
      attr.items = _.orderBy(items, 'rank');
      return attr;
    });
    return _.orderBy(flat, 'groupId');
  }
}

export class FlatComplexAttributeValue {
  public createdBy: string;
  public createdOn: Date;
  public updatedBy: string;
  public updatedOn: Date;
  public groupId: number;
  public items: ComplexAttributeItems[];
}

export class ComplexAttributeItems {
  constructor(
    subFieldDefinitionItemId: string,
    subFieldValues: ComplexAttributeSubFieldValue[],
    name: string,
    type: string,
    rank: number
  ) {
    this.subFieldDefinitionItemId = subFieldDefinitionItemId;
    this.subFieldValues = subFieldValues;
    this.name = name;
    this.type = type;
    this.rank = rank;
    this.isDateType = this.type === AttributeUiType.Date;
    this.isDropDownType = this.type === AttributeUiType.DropDown;
    this.isNumberType = this.type === AttributeUiType.Number;
    this.isShortAnswerType = this.type === AttributeUiType.ShortAnswer;
    this.isLongAnswerType = this.type === AttributeUiType.LongAnswer;
    this.isEncryptedFieldType = this.type === AttributeUiType.EncryptedField;
    this.isCheckboxType = this.type === AttributeUiType.Checkboxes;
    this.isMultiSelectType = this.type === AttributeUiType.MultiSelect;
    this.isSkillType = this.type === AttributeUiType.Skill;
    this.isStarRatingType = this.type === AttributeUiType.StarRating;
    this.isDateRangeType = this.type === AttributeUiType.DateRange;
    this.allowsMultiple =
      this.type == AttributeUiType.Checkboxes || this.type == AttributeUiType.MultiSelect;
    if (this.allowsMultiple) {
      this.values = this.subFieldValues
        .map((a) => ComplexAttributeItems.formatDisplayValue(a))
        .filter((a) => a);
    } else {
      this.value = ComplexAttributeItems.formatDisplayValue(this.subFieldValues[0]);
      this.rawValue = this.subFieldValues[0].value;
    }
  }
  public subFieldDefinitionItemId: string;
  public name: string;
  public type: string;
  public rank: number;
  public subFieldValues: ComplexAttributeSubFieldValue[];
  public isDateType: boolean;
  public isDropDownType: boolean;
  public isNumberType: boolean;
  public isShortAnswerType: boolean;
  public isLongAnswerType: boolean;
  public isEncryptedFieldType: boolean;
  public isCheckboxType: boolean;
  public isMultiSelectType: boolean;
  public isSkillType: boolean;
  public isStarRatingType: boolean;
  public isDateRangeType: boolean;
  public value: string;
  public values: string[];
  public rawValue: string;
  public allowsMultiple: boolean;

  private static formatDisplayValue(item: ComplexAttributeSubFieldValue): string {
    return item
      ? FieldDisplayPipe.GenerateDisplayValue(item.value, item.hasValue(), item.type)
      : '';
  }
}
